<template>
  <div :class="['page-wrapper', { 'moving-variant': isMovingVariant }]">
    <section :class="['hero-panel', { 'moving-hero-panel': isMovingVariant }]">
      <div v-if="isMovingVariant" class="moving-hero-viewport">
        <img :src="heroImageSrc" class="hero-image moving-hero-image" alt="Battle League Gaming moving hero image">
      </div>
      <img v-else :src="heroImageSrc" class="hero-image" alt="Battle League Gaming hero image">
      <div class="hero-shadow"></div>
      <div class="overlay-image-wrapper">
        <img :src="require(`../../brand/${this.brandConfig.brand}/assets/logo-caption.png`)" class="overlay-image" alt="Battle League Gaming">
      </div>
    </section>

    <section :class="['content-panel', { 'moving-content-panel': isMovingVariant }]">
      <div v-if="brandConfig.heroTagline || brandConfig.price" class="service-price-block">
        <div v-if="brandConfig.price" class="service-price">
          {{ this.brandConfig.price }}
        </div>
        <div v-if="brandConfig.heroTagline" class="service-tagline">
          {{ this.brandConfig.heroTagline }}
        </div>
      </div>

      <div class="msisdn-input-wrapper">
        <div class="msisdn-field">
          <span class="msisdn-prefix">
            <svg
              class="msisdn-phone-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <rect x="7" y="2" width="10" height="20" rx="2" stroke="currentColor" stroke-width="1.6" />
              <line x1="10.5" y1="18.5" x2="13.5" y2="18.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            </svg>
            +65
          </span>
          <input
            :value="localMsisdn"
            class="msisdn-input"
            type="tel"
            inputmode="numeric"
            autocomplete="tel"
            :placeholder="this.brandConfig.phoneNumberPrompt"
            aria-label="Mobile number"
            @input="onMsisdnInput"
          >
        </div>
      </div>

      <button class="accept-btn" @click="handleSubscription">
        {{ this.brandConfig.subscribeButton }}
      </button>

      <div v-if="inlineErrorMessage" class="inline-error-message">
        {{ inlineErrorMessage }}
      </div>

      <div v-if="outroCopy" class="outro-copy">
        {{ outroCopy }}
      </div>

      <section v-if="isMovingVariant" class="battlegrounds-section">
        <div class="battlegrounds-label">
          {{ this.brandConfig.battlegroundsLabel }}
        </div>
        <img :src="battlegroundGroupSrc" class="battlegrounds-group-image" alt="Battle League Gaming battlegrounds">
      </section>

      <div v-if="brandConfig.subscriptionDisclosure" class="legal-copy">
        {{ this.brandConfig.subscriptionDisclosure }}
      </div>

      <div @click="onTsAndCsClicked" class="terms-and-conditions">
        {{ this.brandConfig.termsAndConditionsText }}
      </div>

      <div @click="handleUnsubscribe" class="unsubscribe-link">
        {{ this.brandConfig.unsubscribeLinkText }}
      </div>

      <div v-if="unsubMessage" :class="['unsubscribe-message', `unsubscribe-message-${unsubMessageType}`]">
        {{ unsubMessage }}
      </div>
    </section>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import { ref } from "vue";
import SingtelSubscriptionService from "@/services/singtelSubscriptionService";
import { trackEvent } from "@/functions/trackEvent";

const isLoading = ref(false);
const SESSION_ID_STORAGE_KEY = "singtelSessionId";
const MSISDN_STORAGE_KEY = "singtelMsisdn";
const ALLOWED_SUBSCRIBE_RESULT_CODES = [100, 150, 151];
// Singapore mobile number: 8 digits starting with 8 or 9 (before the +65 prefix).
const SG_MOBILE_LOCAL_REGEX = /^[89]\d{7}$/;

export default {
  data() {
    return {
      localMsisdn: "",
      inlineErrorMessage: "",
      unsubMessage: "",
      unsubMessageType: "error",
      transaction_id: undefined,
    };
  },
  computed: {
    ...mapState(["brandConfig", "msisdn", "sessionId"]),
    isMovingVariant() {
      return this.$route?.path === "/moving";
    },
    heroImageSrc() {
      if (!this.brandConfig?.brand) {
        return "";
      }

      if (this.isMovingVariant) {
        return require(`../../brand/${this.brandConfig.brand}/assets/moving/animationBanner.png`);
      }

      return require(`../../brand/${this.brandConfig.brand}/assets/evina-img.webp`);
    },
    battlegroundGroupSrc() {
      return require(`../../brand/${this.brandConfig.brand}/assets/moving/BattlegroundGroup.png`);
    },
    subHeaderParts() {
      const text = this.brandConfig?.subHeaderText?.trim() || "";
      const parts = text.match(/[^.!?á¢]+[.!?á¢]?/g)?.map((part) => part.trim()).filter(Boolean) || [];

      if (!parts.length) {
        return {
          intro: "",
          outro: "",
        };
      }

      if (parts.length === 1) {
        return {
          intro: parts[0],
          outro: "",
        };
      }

      return {
        intro: parts[parts.length - 1],
        outro: parts.slice(0, -1).join(" "),
      };
    },
    outroCopy() {
      return this.subHeaderParts.outro;
    },
  },
  async mounted() {
    this.setTransactionId();
    trackEvent("landing_page_view", {
      transaction_id: this.transaction_id,
      variant: this.isMovingVariant ? "moving" : "standard",
    });
    this.handleCallbackSession();
  },
  methods: {
    ...mapMutations(["setMsisdn", "setSessionId"]),
    setTransactionId() {
      const urlParams = new URLSearchParams(window.location.search);
      this.transaction_id = urlParams.get("transaction_id");
    },
    onTsAndCsClicked() {
      window.open(this.brandConfig.termsAndConditionsUrl, "_blank");
    },

    persistMsisdn(msisdn) {
      sessionStorage.setItem(MSISDN_STORAGE_KEY, msisdn);
      this.setMsisdn(msisdn);
    },

    persistSessionId(sessionId) {
      if (!sessionId) {
        return;
      }

      sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
      this.setSessionId(sessionId);
    },

    handleCallbackSession() {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionIdFromQuery = urlParams.get("sessionId");

      if (!sessionIdFromQuery) {
        const persistedMsisdn = sessionStorage.getItem(MSISDN_STORAGE_KEY);
        const persistedSessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);

        if (persistedMsisdn && !this.msisdn) {
          this.setMsisdn(persistedMsisdn);
        }

        if (persistedSessionId && !this.sessionId) {
          this.setSessionId(persistedSessionId);
        }

        return;
      }

      this.persistSessionId(sessionIdFromQuery);
      this.$router.replace({
        path: "/submit-otp",
        query: { sessionId: sessionIdFromQuery },
      });
    },

    onMsisdnInput(event) {
      this.inlineErrorMessage = "";

      let digits = (event.target.value || "").replace(/\D/g, "");

      if (digits.startsWith("65") && digits.length > 8) {
        digits = digits.slice(2);
      }
      digits = digits.replace(/^0+/, "");

      digits = digits.slice(0, 8);

      this.localMsisdn = digits;
      event.target.value = digits;
    },

    resolveMsisdn() {
      const localNumber = (this.localMsisdn || "").replace(/\D/g, "");
      return localNumber ? `65${localNumber}` : "";
    },

    redirectToPlatform() {
      const msisdn = this.$store.state.msisdn;
      const platformUrl = msisdn
        ? `${this.brandConfig.platformUrl}?msisdn=${encodeURIComponent(msisdn)}`
        : this.brandConfig.platformUrl;
      window.location.href = platformUrl;
    },

    getTrackingParams() {
      return {
        transaction_id: this.transaction_id,
      };
    },
    
    async handleSubscription() {
      trackEvent("subscribe_click", { transaction_id: this.transaction_id });

      isLoading.value = true;
      this.inlineErrorMessage = "";

      const msisdn = this.resolveMsisdn();
      if (!msisdn) {
        this.inlineErrorMessage = this.brandConfig.phoneNumberEmptyText;
        isLoading.value = false;
        return;
      }

      if (!SG_MOBILE_LOCAL_REGEX.test(this.localMsisdn)) {
        this.inlineErrorMessage = this.brandConfig.phoneNumberInvalidText;
        isLoading.value = false;
        return;
      }

      this.persistMsisdn(msisdn);

      try {
        const checkResponse = await SingtelSubscriptionService.checkSubscription(msisdn);

        if (checkResponse?.isSubscribed === true) {
          this.redirectToPlatform();
          return;
        }

        const subscribeResponse = await SingtelSubscriptionService.subscribe(msisdn, this.getTrackingParams());
        const sessionId = subscribeResponse?.sessionId;
        const redirectUrl = subscribeResponse?.redirectUrl;

        if (sessionId) {
          this.persistSessionId(sessionId);
        }

        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }

        if (ALLOWED_SUBSCRIBE_RESULT_CODES.includes(subscribeResponse?.resultCode) && sessionId) {
          this.$router.push({
            path: "/submit-otp",
            query: { sessionId },
          });
          return;
        }

        this.inlineErrorMessage =
          subscribeResponse?.resultText || "The Singtel subscribe response did not include a redirect or session.";
      } catch (error) {
        console.error("=== Subscription Error ===");
        console.error("Error:", error);
        console.error("Error response:", error.response);
        this.inlineErrorMessage =
          error.response?.data?.resultText ||
          error.response?.data?.message ||
          error.message ||
          "An unknown error occurred.";
      } finally {
        isLoading.value = false;
      }
    },

    async handleUnsubscribe() {
      if (isLoading.value) {
        return;
      }

      this.unsubMessage = "";

      const msisdn = this.resolveMsisdn();
      if (!msisdn) {
        this.unsubMessageType = "error";
        this.unsubMessage = this.brandConfig.unsubscribeEmptyText;
        return;
      }

      isLoading.value = true;

      try {
        await SingtelSubscriptionService.unsubscribe(msisdn);
        this.unsubMessageType = "success";
        this.unsubMessage = this.brandConfig.unsubscribeSuccessText;
      } catch (error) {
        this.unsubMessageType = "error";

        if (error.response?.status === 404) {
          this.unsubMessage = this.brandConfig.unsubscribeNotFoundText;
        } else {
          this.unsubMessage =
            error.response?.data?.resultText ||
            error.response?.data?.message ||
            error.message ||
            "An unknown error occurred.";
        }
      } finally {
        isLoading.value = false;
      }
    },
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: #020202;
  color: #FFFFFF;
  display: flex;
  flex-direction: column;
}

.hero-panel {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.moving-hero-panel {
  background: #020202;
}

.moving-hero-viewport {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  min-height: 64vh;
  max-height: 74vh;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.moving-hero-image {
  width: 190%;
  max-width: none;
  height: 100%;
  object-fit: cover;
  object-position: left center;
  animation: moving-banner-sweep 18s linear infinite;
  will-change: transform;
}

.hero-shadow {
  position: absolute;
  inset: 0;
  background:
      linear-gradient(180deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0) 14%, rgba(0, 0, 0, 0.18) 58%, rgba(2, 2, 2, 0.96) 100%),
      linear-gradient(180deg, rgba(2, 2, 2, 0) 0%, rgba(2, 2, 2, 0.9) 92%, #020202 100%);
}

.overlay-image-wrapper {
  position: absolute;
  left: 50%;
  bottom: 7%;
  bottom: 25%;
  width: min(84%, 360px);
  transform: translateX(-50%);
  pointer-events: none;
  z-index: 2;
}

.overlay-image {
  width: 100%;
  height: auto;
  display: block;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5));
}

.moving-variant .overlay-image-wrapper {
  bottom: 34%;
}

.content-panel {
  width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  margin-top: -8rem;
  padding: 0 1.15rem 2rem;
  position: relative;
  z-index: 3;
}

.moving-content-panel {
  gap: 1.75rem;
  margin-top: -9.5rem;
  padding-top: 0.75rem;
}

.outro-copy,
.legal-copy,
.terms-and-conditions,
.unsubscribe-link,
.unsubscribe-message {
  text-align: center;
}

.service-price-block {
  width: 353px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  text-align: center;
}

.service-tagline {
  color: #FFFFFF;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}

.service-price {
  color: #B3F50D;
  font-family: "Clash Display", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.1;
}

.msisdn-input-wrapper {
  width: 353px;
  max-width: 100%;
}

.msisdn-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 56px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.msisdn-field:focus-within {
  border-color: rgba(179, 245, 13, 0.85);
  box-shadow: 0 0 0 3px rgba(179, 245, 13, 0.15);
}

.msisdn-prefix {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  height: 100%;
  padding: 0 0.85rem;
  color: #FFFFFF;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.04);
  border-right: 1px solid rgba(255, 255, 255, 0.18);
}

.msisdn-phone-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.msisdn-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  border-radius: 0;
  background: transparent;
  color: #FFFFFF;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  padding: 0 1rem;
  outline: none;
}

.msisdn-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.accept-btn {
  width: 353px;
  max-width: 100%;
  height: 64px;
  border: none;
  border-radius: 5px;
  background: #B3F50D;
  color: #000;
  cursor: pointer;
  font-family: "Clash Display", sans-serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  padding: 0 1rem;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accept-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.4);
}

.inline-error-message {
  width: 353px;
  max-width: 100%;
  color: #ff8e8e;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.35;
  text-align: center;
}

.outro-copy {
  max-width: 21rem;
  color: #C4C4C4;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.battlegrounds-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.battlegrounds-label {
  width: 208px;
  max-width: 100%;
  color: #C4C4C4;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
}

.battlegrounds-group-image {
  width: min(100%, 360px);
  height: auto;
  display: block;
}

.legal-copy {
  max-width: 22rem;
  margin-top: auto;
  color: rgba(255, 255, 255, 0.7);
  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  line-height: 1.45;
}

.terms-and-conditions {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  text-decoration: underline;
  cursor: pointer;
}

.unsubscribe-link {
  margin-top: -1rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  text-decoration: underline;
  cursor: pointer;
}

.unsubscribe-message {
  max-width: 22rem;
  color: #ff8e8e;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.35;
}

.unsubscribe-message-success {
  color: #B3F50D;
}

@keyframes moving-banner-sweep {
  0% {
    transform: translateX(0);
  }

  100% {
    transform: translateX(-47.5%);
  }
}

@media (min-width: 768px) {
  .hero-image {
    min-height: 70vh;
  }

  .moving-hero-viewport {
    min-height: 70vh;
  }

  .moving-variant .overlay-image-wrapper {
    bottom: 30%;
    width: min(54%, 320px);
  }

  .content-panel {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .moving-content-panel {
    margin-top: -8.5rem;
  }

  .battlegrounds-group-image {
    width: min(100%, 420px);
  }
}
</style>
